/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import Icon from 'components/Icon/Icon';
import Image from 'components/Image/Image';
import PhotoPreviewer from 'components/Modal/PhotoPreviewer/PhotoPreviewer';
import { isEmpty } from 'ramda';
import Accordion from 'components/Accordian/Accordian';
import { VISIT_STATUS, visitTypes } from 'context/visitContext';
import VisitItem from 'components/VisitItem/VisitItem';
import Graphic from 'components/Graphic/Graphic';
import { getMessages, loadReceivedMessage } from 'context/msgContext';
import { isMobile, isTablet } from 'react-device-detect';
import NoteLists from 'components/NoteLists/NoteLists';
import {
  getFollowUpRequest,
  getLabs,
  getMedications,
  getQuestionnaires,
  getAskQnAs,
  getQuestions,
  syncPatientWithPhoton,
  loadReceivedAnswer,
} from '../../api/api';
import { getNotes, useAppContext, showAlert } from '../../context/appContext';
import { paths } from '../../routes';
import {
  Answer,
  HTTPError,
  MessagesQueryType,
  Note,
  Patient,
  QuestionAnswers,
} from '../../types/types';
import readAWSS3File from '../../utils/readAWSS3File';
import {
  capitalize,
  formatPhoneNumber,
  formatToFullAddress,
  inchesToFeetAndInches,
  isRole,
} from '../../utils/utils';

export const questionsQuery = (type: string) => ({
  queryKey: ['questions', type],
  queryFn: () => getQuestions(type),
});

export const questionnairesQuery = (params: Record<string, any>) => ({
  queryKey: ['questionnaires', params],
  queryFn: () => getQuestionnaires(params),
});

export const askQnAsQuery = (visitId: string) => ({
  queryKey: ['ask-QnAs', visitId],
  queryFn: () => getAskQnAs(visitId),
});

export const labsQuery = (params: Record<string, any>) => ({
  queryKey: ['labs', params],
  queryFn: () => getLabs(params),
});

export const notesQuery = (patient: Patient, sortType: 'ALL' | 'PRIVATE' | 'PRIVATE_ADMIN') => ({
  queryKey: ['notes', { sortType, patient }],
  queryFn: () => getNotes({ sortType, patient }),
});

export const medicationsQuery = (params: Record<string, any>) => ({
  queryKey: ['medications', params],
  queryFn: () => getMedications(params),
});

export const messagesQuery = (params: MessagesQueryType) => ({
  queryKey: ['messages', params],
  queryFn: ({ pageParam = 0 }) => getMessages({ page: pageParam, ...params }),
});

export const messageListenerQuery = (params: MessagesQueryType) => ({
  queryKey: ['message-listener', params],
  queryFn: () => loadReceivedMessage(params),
});

export const askQnAsListenerQuery = (visitId: string, qnAsId?: string) => ({
  queryKey: ['ask-QnAs-listener', visitId, qnAsId],
  queryFn: () => loadReceivedAnswer(visitId, qnAsId),
});

export const followUpsQuery = (followUps?: Array<Answer>) => ({
  queryKey: ['follow-ups', followUps],
  queryFn: () => getFollowUpRequest(followUps),
});

function PatientRecords() {
  const [{ user, patients }, dispatch] = useAppContext();
  const [syncedAvatarPhoto, setSyncedAvatarPhoto] = React.useState('');
  const [showPhotoPreviewer, setShowPhotoPreviewer] = React.useState(false);
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const patient = patients.find(fndPatient => fndPatient.id === patientId);

  const { data, isLoading, isFetching } = useQuery({
    ...questionnairesQuery({ patientId: patient?.id }),
    enabled: !!(isEmpty(patient?.questionnaires) && patient?.id),
  });

  const questionnaires = (data || patient?.questionnaires || []) as QuestionAnswers[];

  const completedVisits = questionnaires.filter(
    fndQuestion => fndQuestion.status === VISIT_STATUS.COMPLETE,
  );

  const pendingVisits = questionnaires.filter(
    fndQuestion => fndQuestion.status === VISIT_STATUS.UNANSWERED,
  );

  const { mutate: syncPatientWithPhotonFn } = useMutation(
    (variables: { patientId: string }) => syncPatientWithPhoton(variables.patientId),
    {
      onSuccess: (result, variables) => {
        const { photonId } = result;

        const updatePatients = patients.map(fndPatient => ({
          ...fndPatient,
          ...(fndPatient.id === variables.patientId && {
            photonId,
          }),
        })) satisfies Patient[];

        dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: updatePatients });
      },
      onError: (error: HTTPError) => {
        console.error('patient photon sync error', error?.message);
      },
    },
  );

  React.useEffect(() => {
    if (!patient?.id || isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')) return;
    syncPatientWithPhotonFn({ patientId: patient.id });
  }, [patient?.id, syncPatientWithPhotonFn, user?.roles]);

  React.useEffect(() => {
    if (patient && !patient.signedAvatar) {
      (async function syncAvatarPhoto() {
        setSyncedAvatarPhoto(await readAWSS3File('avatar', patient.profilePicture));
      })();
    }
  }, [dispatch, patient]);

  const handleVisitClicked = (visit: QuestionAnswers) => {
    const type = visit.type.toLocaleLowerCase();
    const from = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    const tabId = '';

    navigate(
      `${paths.VISIT_RECORDS_URL_PATH.replace(':visitId', visit.id)}${tabId}?type=${type}&patient=${
        visit.patientUserId
      }&from=${from}`,
    );
  };

  const handleSyncAddedNote = (newNote: Note) => {
    const updatePatients = patients.map(fndPatient => ({
      ...fndPatient,
      ...(fndPatient.id === patient?.id && {
        patientDetails: {
          ...fndPatient.patientDetails,
          extendedNotes: [newNote, ...(fndPatient?.patientDetails?.extendedNotes || [])],
        },
      }),
    })) satisfies Patient[];

    dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: updatePatients });
  };

  const getPatientDOBAndGender = (fndPatient: Patient) => {
    const dob =
      moment().diff(moment(fndPatient.patientDetails?.dateOfBirth).format('YYYY-MM-DD'), 'years') ||
      0;

    if (dob && fndPatient.patientDetails?.gender)
      return `${dob}, ${capitalize(fndPatient.patientDetails.gender)}`;

    if (dob) return `${dob} years`;

    if (fndPatient.patientDetails?.gender) return capitalize(fndPatient.patientDetails.gender);

    return '-';
  };

  if (!patient) return null;

  return (
    <>
      <div className="flex flex-col tablet:-mx-10">
        <div className="flex flex-col py-4 px-4 tablet:px-8">
          <div className="flex items-center tablet:space-x-8 tablet:px-10">
            <button
              onClick={() => navigate(paths.HOME_URL_PATH)}
              type="button"
              className="hidden tablet:flex flex-row items-center"
            >
              <Icon id="goBack" className="w-8 h-8" />
            </button>
            <div className="flex items-center space-x-5 shrink-0">
              <Image
                className="h-[70px] w-[70px] tablet:h-[80px] tablet:w-[80px] rounded-full shrink-0"
                src={
                  patient.signedAvatar ||
                  syncedAvatarPhoto ||
                  'image_patient_avatar_placeholder.png'
                }
                alt={patient ? `${patient.firstName} ${patient.lastName}` : 'River Patient'}
                bgColor={patient.avatarPlaceholderColor}
              />
              <div>
                <h1 className="font-medium text-[20px]">
                  {patient ? `${patient.firstName} ${patient.lastName}` : 'River Patient'}
                </h1>
                <p className="text-sm">{patient ? getPatientDOBAndGender(patient) : '-'}</p>
              </div>
            </div>
          </div>

          <hr className="block border-b-[1px] border-t-0 border-[#e9ecef] tablet:mx-28 mt-7" />

          <div className="tablet:mx-28 mb-5">
            <Accordion
              open={!(isMobile && !isTablet)}
              id="about"
              title={
                <div className="flex flex-row justify-between w-full">
                  <span className="text-neutral-500 font-medium">ABOUT RECORDS</span>
                </div>
              }
            >
              <AboutView
                patient={patient}
                onShowPhotoPreviewer={() => setShowPhotoPreviewer(true)}
              />
            </Accordion>

            {!isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') && (
              <Accordion
                id="pending-visits"
                title={({ isOpen }) => {
                  return (
                    <div className="flex flex-row justify-between w-full">
                      <span className="text-neutral-500 font-medium">PENDING VISITS</span>
                      <span className="text-river-blue inline-flex items-center text-xs font-medium">
                        {isOpen ? 'CLOSE' : 'VIEW ALL'}
                      </span>
                    </div>
                  );
                }}
              >
                <VisitsView
                  key="pending"
                  status="pending"
                  isLoading={(isLoading && isFetching) || !pendingVisits}
                  visits={pendingVisits}
                  onVisitClicked={handleVisitClicked}
                />
              </Accordion>
            )}

            <Accordion
              id="completed-visits"
              title={({ isOpen }) => {
                return (
                  <div className="flex flex-row justify-between w-full">
                    <span className="text-neutral-500 font-medium">COMPLETED VISITS</span>
                    <span className="text-river-blue inline-flex items-center text-xs font-medium">
                      {isOpen ? 'CLOSE' : 'VIEW ALL'}
                    </span>
                  </div>
                );
              }}
            >
              <VisitsView
                key="completed"
                status="completed"
                isLoading={(isLoading && isFetching) || !completedVisits}
                visits={completedVisits}
                onVisitClicked={handleVisitClicked}
              />
            </Accordion>

            <Accordion
              id="notes"
              title={({ isOpen }) => {
                return (
                  <div className="flex flex-row justify-between w-full">
                    <span className="text-neutral-500 font-medium">NOTES</span>
                    <span className="text-river-blue inline-flex items-center text-xs font-medium">
                      {isOpen ? 'CLOSE' : 'VIEW ALL'}
                    </span>
                  </div>
                );
              }}
            >
              {user && patient && (
                <NoteLists
                  user={user}
                  patient={patient}
                  onSyncAddedNote={handleSyncAddedNote}
                  onShowAlert={(error: HTTPError) => {
                    showAlert(dispatch, {
                      show: true,
                      type: 'ERROR',
                      message: error.message,
                    });
                  }}
                />
              )}
            </Accordion>
          </div>
        </div>
      </div>

      {showPhotoPreviewer && Boolean(patient.idImage) && (
        <PhotoPreviewer
          photos={[patient.idImage]}
          onCloseModal={() => setShowPhotoPreviewer(false)}
        />
      )}
    </>
  );
}

function VisitsView({
  isLoading,
  visits,
  status,
  onVisitClicked,
}: {
  isLoading: boolean;
  status: string;
  visits: QuestionAnswers[];
  onVisitClicked: (visit: QuestionAnswers) => void;
}) {
  const [visitLimitCount, setVisitLimitCount] = React.useState(10);

  const getVisitTitle = (type: string) => {
    const findVisit = visitTypes.find(fndVisit => fndVisit.type === type.toLocaleUpperCase());
    if (!findVisit) return type;
    return `${findVisit.name}`;
  };

  return (
    <div>
      {isLoading ? (
        <div
          className="mx-auto text-center mt-4 mb-10 tablet:mt-10 tablet:mb-20 w-28 
          flex justify-center items-start flex-col"
        >
          <Icon id="loader" className="w-10 h-10 text-river-black mx-auto" />
          <span className="loading">Please wait</span>
        </div>
      ) : (
        <>
          {visits.length === 0 ? (
            <div className="w-full flex flex-col justify-center items-center">
              {status === 'completed' || status === 'complete' ? (
                <>
                  <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
                  <div className="text-center mt-3">
                    <p className="font-medium">You have no visits completed</p>
                    <p className="text-neutral-500 text-xs">
                      See all the visits you’ve completed in one place
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
                  <div className="text-center mt-3">
                    <p className="font-medium">You’re all caught up on your visits</p>
                    <p className="text-neutral-500 text-xs">See all pending visits in one place</p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-[#e9ecef]">
              {visits.slice(0, visitLimitCount).map(visit => (
                <VisitItem
                  key={visit.id}
                  date={moment(visit.createdAt, 'YYYY-MM-DD').format('MM/DD/YYYY')}
                  iconType={visit.type.toLocaleLowerCase()}
                  status={visit.status === VISIT_STATUS.COMPLETE ? 'completed' : 'pending'}
                  title={getVisitTitle(visit.type)}
                  onVisitClicked={() => onVisitClicked(visit)}
                />
              ))}
            </div>
          )}

          {visits.length > 10 && visitLimitCount === 10 && (
            <div className="text-center mt-3 mb-0 tablet:mt-2 text-river-blue">
              <button
                onClick={() => setVisitLimitCount(visits.length)}
                type="button"
                className="p-2 !font-medium"
              >
                Show more visits...
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AboutView({
  patient,
  onShowPhotoPreviewer,
}: {
  patient: Patient;
  onShowPhotoPreviewer: () => void;
}) {
  const getPatientDOB = () => {
    const dob =
      moment().diff(moment(patient.patientDetails?.dateOfBirth).format('YYYY-MM-DD'), 'years') || 0;

    if (dob) return dob;

    return '-';
  };

  const getPatientPlan = (plan: string) => {
    return plan === 'COMPLETE-CARE'
      ? 'Complete Care'
      : plan === 'VIRTUAL-CARE'
      ? 'Virtual Care'
      : plan === 'STARTER-CARE'
      ? 'Starter Care'
      : 'Primary Care';
  };

  return (
    <div className="flex flex-col space-y-4 tablet:space-y-6">
      {/* Grid One */}
      <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
        <div>
          <span className="text-sm font-medium">Plan Type</span>
          <p className="text-[15px] text-stone-500">
            {patient?.patientDetails?.planType || 'River health'}
          </p>
        </div>

        <div>
          <span className="text-sm font-medium">Subscription Type</span>
          <p className="text-[15px] text-stone-500">
            {getPatientPlan(patient?.patientDetails?.subscriptionType)}
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Family Plan Type</span>
            <p className="text-[15px] text-stone-500">{patient?.patientDetails?.planFamilyType}</p>
          </div>
        </div>

        <div className="hidden tablet:flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Identification</span>
            <p className="text-[15px] text-stone-500">
              <button
                onClick={onShowPhotoPreviewer}
                disabled={isEmpty(patient.idImage)}
                type="button"
                className="underline text-river-blue underline-offset-4 disabled:opacity-50"
              >
                Click to view
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Grid Two */}
      <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
        <div>
          <span className="text-sm font-medium">Preferred Pronoun</span>
          <p className="text-[15px] text-stone-500">
            {capitalize(patient.preferredPronouns) || '-'}
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Preferred Name</span>
            <p className="text-[15px] text-stone-500">{patient.preferredName || '-'}</p>
          </div>
        </div>

        <div>
          <span className="text-sm font-medium">Gender at Birth</span>
          <p className="text-[15px] text-stone-500">
            {capitalize(patient.patientDetails?.gender) || '-'}
          </p>
        </div>

        <div>
          <span className="text-sm font-medium">Gender Identity</span>
          <p className="text-[15px] text-stone-500 ">
            {capitalize(patient.patientDetails?.genderIdentity) || '-'}
          </p>
        </div>
      </div>

      {/* Grid Three */}
      <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
        <div>
          <span className="text-sm font-medium">Age</span>
          <p className="text-[15px] text-stone-500">
            {getPatientDOB()}{' '}
            <span>
              (
              {patient.patientDetails?.dateOfBirth
                ? moment(patient.patientDetails?.dateOfBirth, 'YYYY-MM-DD').format('DD MMMM YYYY')
                : ''}
              )
            </span>
          </p>
        </div>

        <div>
          <span className="text-sm font-medium">Height</span>
          <p className="text-[15px] text-stone-500">
            {patient?.patientDetails?.height
              ? `${inchesToFeetAndInches(patient.patientDetails?.height || 0).feet}'${
                  inchesToFeetAndInches(patient.patientDetails?.height || 0).inches
                }"`
              : '-'}
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Weight</span>
            <p className="text-[15px] text-stone-500">
              {patient?.patientDetails?.weight ? `${patient?.patientDetails?.weight} lbs` : '-'}
            </p>
          </div>
        </div>

        <div>
          <span className="text-sm font-medium">Phone number</span>
          <p className="text-[15px] text-stone-500">
            {patient.phoneNumber ? formatPhoneNumber(patient.phoneNumber || '') : '-'}
          </p>
        </div>
      </div>

      {/* Grid Four */}
      <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Address</span>
            <p className="text-[15px] text-stone-500">{patient?.streetAddress}</p>
            <p className="text-[15px] text-stone-500">
              {formatToFullAddress([
                '',
                patient?.apartment,
                patient?.city,
                patient?.state,
                `${patient?.zipCode || ''}`,
              ])}
            </p>
          </div>
        </div>

        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Zip code</span>
            <p className="text-[15px] text-stone-500">{patient.zipCode}</p>
          </div>
        </div>

        <div>
          <span className="text-sm font-medium">Email</span>
          <p className="text-[15px] text-stone-500">{patient.email || '-'}</p>
        </div>

        <div className="flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Emergency Contact</span>
            <p className="text-[15px] text-stone-500">
              {patient?.patientDetails?.emergencyContactFirstName
                ? `${patient?.patientDetails?.emergencyContactFirstName} ${patient?.patientDetails?.emergencyContactLastName}`
                : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Grid Five */}
      <div className="grid grid-cols-1 gap-4 tablet:gap-0 tablet:grid-cols-2 laptop:grid-cols-4">
        <div>
          <span className="text-sm font-medium">Emergency Number</span>
          <p className="text-[15px] text-stone-500">
            {patient?.patientDetails?.emergencyContactPhoneNumber
              ? formatPhoneNumber(patient?.patientDetails?.emergencyContactPhoneNumber || '')
              : '-'}
          </p>
        </div>

        {patient?.familyManager ? (
          <>
            <div>
              <span className="text-sm font-medium">Family Manager Name</span>
              <p className="text-[15px] text-stone-500">
                {patient?.familyManager?.firstName} {patient?.familyManager?.lastName}
              </p>
            </div>

            <div>
              <span className="text-sm font-medium">Family Manager Phone</span>
              <p className="text-[15px] text-stone-500">
                {formatPhoneNumber(patient?.familyManager?.phoneNumber || '')}
              </p>
            </div>
          </>
        ) : null}

        <div className="tablet:hidden flex flex-col space-y-4">
          <div>
            <span className="text-sm font-medium">Identification</span>
            <p className="text-[15px] text-stone-500">
              <button
                onClick={onShowPhotoPreviewer}
                disabled={isEmpty(patient.idImage)}
                type="button"
                className="underline text-river-blue underline-offset-4 disabled:opacity-50"
              >
                Click to view
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PatientRecords;
